/**
 * Admin Portal → Codes → All codes: every code Coach Cedar issued, in one list (Codes PR-8, on
 * `GET /api/admin/codes/catalog`, Codes PR-7). Filters, server-side pages, the filtered CSV and one
 * code's drill-down (its redemptions, grant, failed attempts and the admin actions on it).
 *
 * The SAME component is embedded on a partner's Codes tab, pre-filtered to that partner (`fixed`), so
 * the partner page and the Codes area read one list.
 *
 * Roles: every admin role reads. Support and viewer see codes masked and no person data (the server
 * drops them), may search a code only exactly (a prefix search would spell a masked code out), and get
 * no export and no revoke — both hidden until the role is known (fail closed; the server answers 403
 * anyway).
 *
 * Web-only (the admin SPA).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminButton, AdminCard, Badge } from '../../../components/admin/ui';
import { PartnersAdmin } from '../../../services/partnersAdminService';
import { Summer50GrowthService } from '../../../services/summer50GrowthService';
import {
  NoticeLine, SAVE_FAILED, fmtDate, fmtDateTime, hint, input, label, row, saveBlob, td, th, type Notice,
} from '../partners/PartnersShared';
import { shortId } from './codesAdminLogic';
import { CodesAdmin } from './codesAdminService';
import {
  CATALOG_PAGE_SIZE, CREATED_VIA_OPTIONS, KIND_LABELS, UNIFIED_STATUSES, UNIFIED_STATUS_LABELS, UNIFIED_VOCAB_NOTE,
  familyLabel, familyOptions, familyPagePath, ownerText, pageLine, redemptionLine, revocable, statusTone, usesText,
  type CatalogFilters, type CatalogRow, type CatalogSummary, type CodeDetail,
} from './codesAreaLogic';
import { ErrorLine, VocabNote, readModelError } from './CodesArea';

const sel: React.CSSProperties = { ...input, minWidth: 120 };

export const CodeCatalogPanel: React.FC<{
  /** Filters the admin cannot change (a partner's page passes its partner). */
  fixed?: CatalogFilters;
  /** Filters to start with (the page's address). */
  initial?: CatalogFilters;
  writable: boolean;
  roleLoaded: boolean;
}> = ({ fixed, initial, writable, roleLoaded }) => {
  const [draft, setDraft] = useState<CatalogFilters>({ ...initial });
  const [filters, setFilters] = useState<CatalogFilters>({ ...initial });
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<{ codes: CatalogRow[]; total: number; masked: boolean } | null>(null);
  const [summary, setSummary] = useState<CatalogSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const effective: CatalogFilters = { ...filters, ...fixed };

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await CodesAdmin.catalog({ ...filters, ...fixed }, { limit: CATALOG_PAGE_SIZE, offset }, writable);
      setPage({ codes: r.codes || [], total: r.total || 0, masked: !!r.codes_masked });
    } catch (e) {
      setPage(null);
      setError(readModelError(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), JSON.stringify(fixed), offset, writable]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    CodesAdmin.catalogSummary().then(setSummary).catch(() => setSummary(null));
  }, []);

  const set = (k: keyof CatalogFilters) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setDraft((d) => ({ ...d, [k]: e.target.value }));
  const apply = () => { setOffset(0); setFilters({ ...draft }); };
  const clear = () => { setOffset(0); setDraft({}); setFilters({}); };

  const doExport = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const { blob, filename } = await CodesAdmin.exportCatalog(effective);
      if (!saveBlob(blob, filename)) setNotice({ kind: 'error', text: SAVE_FAILED });
    } catch (e) {
      setNotice({ kind: 'error', text: readModelError(e) });
    } finally {
      setBusy(false);
    }
  };

  const shown = page?.codes ?? [];
  const fixedFamily = !!fixed?.family;
  return (
    <div data-testid="code-catalog">
      <VocabNote text={UNIFIED_VOCAB_NOTE} />
      <AdminCard style={{ marginBottom: 12 }}>
        <div style={row}>
          <div>
            <label style={label} htmlFor="cat-code">Code</label>
            <input id="cat-code" style={input} value={draft.code ?? ''} onChange={set('code')}
              placeholder={writable ? 'SUMMER-7F3K or its start' : 'the whole code'}
              onKeyDown={(e) => { if (e.key === 'Enter') apply(); }} />
          </div>
          {writable && (
            <div>
              <label style={label} htmlFor="cat-match">Match</label>
              <select id="cat-match" style={sel} value={draft.match ?? 'exact'} onChange={set('match')}>
                <option value="exact">Whole code</option>
                <option value="prefix">Starts with</option>
              </select>
            </div>
          )}
          {!fixedFamily && (
            <div>
              <label style={label} htmlFor="cat-family">Family</label>
              <select id="cat-family" style={sel} value={draft.family ?? ''} onChange={set('family')}>
                <option value="">Every family</option>
                {familyOptions(summary).map((f) => <option key={f} value={f}>{familyLabel(f)}</option>)}
              </select>
            </div>
          )}
          <div>
            <label style={label} htmlFor="cat-status">Status</label>
            <select id="cat-status" style={sel} value={draft.status ?? ''} onChange={set('status')}>
              <option value="">Every status</option>
              {UNIFIED_STATUSES.map((s) => <option key={s} value={s}>{UNIFIED_STATUS_LABELS[s]}</option>)}
            </select>
          </div>
          <div>
            <label style={label} htmlFor="cat-kind">Kind</label>
            <select id="cat-kind" style={sel} value={draft.kind ?? ''} onChange={set('kind')}>
              <option value="">Every kind</option>
              {Object.entries(KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={label} htmlFor="cat-via">Made by</label>
            <select id="cat-via" style={sel} value={draft.created_via ?? ''} onChange={set('created_via')}>
              <option value="">Anyone</option>
              {CREATED_VIA_OPTIONS.map((v) => <option key={v} value={v}>{v.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div>
            <label style={label} htmlFor="cat-from">Made from</label>
            <input id="cat-from" type="date" style={input} value={draft.created_from ?? ''} onChange={set('created_from')} />
          </div>
          <div>
            <label style={label} htmlFor="cat-to">to (not including)</label>
            <input id="cat-to" type="date" style={input} value={draft.created_to ?? ''} onChange={set('created_to')} />
          </div>
          <AdminButton size="sm" onClick={apply}>Search</AdminButton>
          <AdminButton size="sm" variant="secondary" onClick={clear}>Clear</AdminButton>
          {writable && (
            <AdminButton size="sm" variant="secondary" disabled={busy} onClick={() => void doExport()}>Export CSV</AdminButton>
          )}
        </div>
        {(filters.batch_id || filters.api_key_id) && (
          <p style={hint}>
            {filters.batch_id && `Batch ${shortId(filters.batch_id)}. `}
            {filters.api_key_id && `Codes one API key made (${shortId(filters.api_key_id)}). `}
          </p>
        )}
        {roleLoaded && !writable && (
          <p style={hint}>Codes are masked for your admin role, and a code is found by its whole name only.</p>
        )}
      </AdminCard>

      <ErrorLine text={error} />
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />

      {page && (
        <>
          <div style={{ ...hint, marginBottom: 6 }}>{pageLine(page.total, offset, shown.length)}</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Code</th><th style={th}>Family</th><th style={th}>Kind</th><th style={th}>Owner</th>
                  <th style={th}>Status</th><th style={th}>Uses</th>
                  {!page.masked && <th style={th}>Note</th>}
                  <th style={th}>Made</th><th style={th}>Expires</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((c) => (
                  <tr key={`${c.source_table}:${c.code_id}`}>
                    <td style={td}>
                      <button type="button" onClick={() => setOpenId(openId === c.code_id ? null : c.code_id)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--cedar-primary, #1F6C4F)' }}
                        aria-expanded={openId === c.code_id}>
                        <code>{c.code}</code>
                      </button>
                    </td>
                    <td style={td}>{familyLabel(c.family)}</td>
                    <td style={td}>{KIND_LABELS[c.kind] ?? c.kind}</td>
                    <td style={td}>{ownerText(c)}</td>
                    <td style={td}>
                      <Badge status={statusTone(c.status)}>{UNIFIED_STATUS_LABELS[c.status] ?? c.status}</Badge>
                      {c.family_status && c.family_status !== c.status && (
                        <div style={{ ...hint, marginTop: 2 }} title="the family's own word">{c.family_status.replace(/_/g, ' ')}</div>
                      )}
                    </td>
                    <td style={td}>{usesText(c)}</td>
                    {!page.masked && (
                      <td style={{ ...td, maxWidth: 240, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                        {c.note || c.handle || c.external_ref || '—'}
                      </td>
                    )}
                    <td style={td}>{fmtDate(c.created_at)}</td>
                    <td style={td}>{fmtDateTime(c.expires_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <AdminButton size="sm" variant="secondary" disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - CATALOG_PAGE_SIZE))}>Previous</AdminButton>
            <AdminButton size="sm" variant="secondary" disabled={offset + shown.length >= page.total}
              onClick={() => setOffset(offset + CATALOG_PAGE_SIZE)}>Next</AdminButton>
          </div>
          {openId && (
            <CodeDetailPanel codeId={openId} writable={writable} onClose={() => setOpenId(null)}
              onChanged={() => void load()} />
          )}
        </>
      )}
    </div>
  );
};

/** One code: its facts, redemptions, grant, failed attempts and the admin actions on it — and, for an admin, revoke. */
export const CodeDetailPanel: React.FC<{ codeId: string; writable: boolean; onClose: () => void; onChanged: () => void }> = ({
  codeId, writable, onClose, onChanged,
}) => {
  const [detail, setDetail] = useState<CodeDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(null);
    CodesAdmin.code(codeId).then(setDetail).catch((e) => setError(readModelError(e)));
  }, [codeId]);
  useEffect(() => { load(); }, [load]);

  const revoke = async (c: CatalogRow) => {
    if (typeof window !== 'undefined' && typeof window.confirm === 'function'
      && !window.confirm(`Revoke ${c.code}? It can never be redeemed again. What someone already redeemed stays theirs.`)) return;
    setBusy(true);
    setNotice(null);
    try {
      if (c.source_table === 'partner_codes') await PartnersAdmin.revokeCode(c.code_id);
      else await Summer50GrowthService.revokeCode(c.code_id);
      setNotice({ kind: 'ok', text: 'Revoked.' });
      load();
      onChanged();
    } catch (e) {
      setNotice({ kind: 'error', text: readModelError(e) });
    } finally {
      setBusy(false);
    }
  };

  const c = detail?.code;
  const familyPage = c ? familyPagePath(c) : null;
  return (
    <AdminCard style={{ marginTop: 16 }} data-testid="code-detail">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <strong>{c ? <code>{c.code}</code> : 'Code'}</strong>
        <div style={{ display: 'flex', gap: 8 }}>
          {c && writable && revocable(c) && (
            <AdminButton size="sm" variant="danger" disabled={busy} onClick={() => void revoke(c)}>Revoke</AdminButton>
          )}
          {familyPage && <Link to={familyPage} style={{ fontSize: 13 }}>Open its page →</Link>}
          <AdminButton size="sm" variant="ghost" onClick={onClose}>Close</AdminButton>
        </div>
      </div>
      <ErrorLine text={error} />
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />
      {c && (
        <div style={{ fontSize: 13, marginTop: 8 }}>
          <div>{familyLabel(c.family)} · {KIND_LABELS[c.kind] ?? c.kind} · {ownerText(c)}</div>
          <div>
            Status: {UNIFIED_STATUS_LABELS[c.status] ?? c.status}
            {c.family_status !== c.status && ` (the family's word: ${c.family_status.replace(/_/g, ' ')})`} · Uses: {usesText(c)}
          </div>
          <div>Made {fmtDateTime(c.created_at)} by {c.created_via}{c.batch_created_by ? ` (admin ${c.batch_created_by})` : ''}
            {c.batch_id && <> · batch <Link to={`/admin/codes/all/batches/${encodeURIComponent(c.batch_id)}`}>{shortId(c.batch_id)}</Link></>}
          </div>
          {c.note && <div>Note: {c.note}</div>}
          {c.grant_duration && <div>Grants: {c.grant_duration.replace(/_/g, ' ')}</div>}
          {detail?.ambiguous && <p style={hint}>Two codes share this id across the lists; showing the first.</p>}

          <h4 style={{ margin: '12px 0 4px' }}>Redemptions ({detail?.redemptions.length ?? 0})</h4>
          {(detail?.redemptions ?? []).length === 0 ? <div style={hint}>None.</div> : (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {detail!.redemptions.map((r) => <li key={r.id}>{fmtDateTime(r.redeemed_at)} — {redemptionLine(r)}</li>)}
            </ul>
          )}
          <h4 style={{ margin: '12px 0 4px' }}>Failed attempts (last 180 days)</h4>
          {(detail?.attempts ?? []).length === 0 ? <div style={hint}>None.</div> : (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {detail!.attempts.map((a, i) => <li key={i}>{fmtDateTime(a.at)} — {a.reason.replace(/_/g, ' ')}{a.user ? ` (${a.user})` : ''}</li>)}
            </ul>
          )}
          <h4 style={{ margin: '12px 0 4px' }}>Admin actions</h4>
          {(detail?.audit ?? []).length === 0 ? <div style={hint}>None.</div> : (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {detail!.audit.map((a, i) => (
                <li key={i}>{fmtDateTime(a.at)} — {a.action.replace(/_/g, ' ')}{a.admin ? ` by admin ${a.admin}` : ''}{a.success ? '' : ' — failed'}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </AdminCard>
  );
};
